import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useSupplierCrmOrgStore } from '../../../store/supplierCrmOrg.store';
import { goalsService, type GoalProgressMode, type GoalType, type PmGoal, type GoalProgressResponse } from '../../../services/projectManagement/goalsService';
import { projectsService, type Project } from '../../../services/projectManagement/projectsService';
import { tasksService, type Task } from '../../../services/projectManagement/tasksService';

const GoalsTab: React.FC = () => {
  const { activeOrgId } = useSupplierCrmOrgStore();

  const [loading, setLoading] = useState(true);
  const [goals, setGoals] = useState<PmGoal[]>([]);
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [progress, setProgress] = useState<GoalProgressResponse | null>(null);

  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);

  const [showCreate, setShowCreate] = useState(false);
  const [createType, setCreateType] = useState<GoalType>('goal');
  const [createTitle, setCreateTitle] = useState('');
  const [createMode, setCreateMode] = useState<GoalProgressMode>('manual');
  const [createTarget, setCreateTarget] = useState<number>(0);
  const [createCurrent, setCreateCurrent] = useState<number>(0);
  const [createDue, setCreateDue] = useState<string>('');

  const [showLink, setShowLink] = useState(false);
  const [linkType, setLinkType] = useState<'project' | 'task'>('project');
  const [linkEntityId, setLinkEntityId] = useState('');

  const fetchGoals = useCallback(async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const { data, error } = await goalsService.list({ organization_id: activeOrgId });
      if (error) {
        toast.error(String(error || 'Failed to load goals'));
        setGoals([]);
        return;
      }
      setGoals(data || []);
      if (!selectedGoalId && data?.[0]?.id) {
        setSelectedGoalId(data[0].id);
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load goals');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, selectedGoalId]);

  const fetchProjectsTasks = useCallback(async () => {
    if (!activeOrgId) return;
    try {
      const [pRes, tRes] = await Promise.all([
        projectsService.list({ organization_id: activeOrgId }),
        tasksService.list({ organization_id: activeOrgId, limit: 200 }),
      ]);
      setProjects(pRes.data || []);
      setTasks(tRes.data || []);
    } catch {
      setProjects([]);
      setTasks([]);
    }
  }, [activeOrgId]);

  const fetchProgress = useCallback(async () => {
    if (!activeOrgId || !selectedGoalId) {
      setProgress(null);
      return;
    }
    setProgressLoading(true);
    try {
      const { data, error } = await goalsService.getProgress(selectedGoalId, activeOrgId);
      if (error) {
        toast.error(String(error || 'Failed to load progress'));
        setProgress(null);
        return;
      }
      setProgress(data);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load progress');
      setProgress(null);
    } finally {
      setProgressLoading(false);
    }
  }, [activeOrgId, selectedGoalId]);

  useEffect(() => {
    void fetchGoals();
    void fetchProjectsTasks();
  }, [fetchGoals, fetchProjectsTasks]);

  useEffect(() => {
    void fetchProgress();
  }, [fetchProgress]);

  const selectedGoal = useMemo(() => goals.find((g) => g.id === selectedGoalId) || null, [goals, selectedGoalId]);

  const createGoal = async () => {
    if (!activeOrgId) return;
    const title = createTitle.trim();
    if (!title) {
      toast.error('Title is required');
      return;
    }

    try {
      const { data, error } = await goalsService.create({
        organization_id: activeOrgId,
        type: createType,
        title,
        progress_mode: createMode,
        target_value: Number(createTarget || 0),
        current_value: Number(createCurrent || 0),
        due_date: createDue || null,
      });

      if (error) {
        toast.error(String(error || 'Failed to create goal'));
        return;
      }

      setShowCreate(false);
      setCreateTitle('');
      setCreateTarget(0);
      setCreateCurrent(0);
      setCreateDue('');

      if (data?.id) setSelectedGoalId(data.id);
      void fetchGoals();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to create goal');
    }
  };

  const saveManualProgress = async () => {
    if (!activeOrgId || !selectedGoalId || !selectedGoal) return;
    try {
      const { error } = await goalsService.update(selectedGoalId, activeOrgId, {
        current_value: Number(selectedGoal.current_value || 0),
        target_value: Number(selectedGoal.target_value || 0),
      } as any);

      if (error) {
        toast.error(String(error || 'Failed to update goal'));
        return;
      }
      void fetchGoals();
      void fetchProgress();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update goal');
    }
  };

  const addLink = async () => {
    if (!activeOrgId || !selectedGoalId) return;
    if (!linkEntityId) {
      toast.error('Select an item to link');
      return;
    }

    try {
      const { error } = await goalsService.addLink(selectedGoalId, {
        organization_id: activeOrgId,
        entity_type: linkType,
        entity_id: linkEntityId,
      });

      if (error) {
        toast.error(String(error || 'Failed to add link'));
        return;
      }

      setShowLink(false);
      setLinkEntityId('');
      void fetchProgress();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to add link');
    }
  };

  const removeLink = async (entityType: 'project' | 'task', entityId: string) => {
    if (!activeOrgId || !selectedGoalId) return;
    try {
      const { error } = await goalsService.removeLink(selectedGoalId, {
        organization_id: activeOrgId,
        entity_type: entityType,
        entity_id: entityId,
      });

      if (error) {
        toast.error(String(error || 'Failed to remove link'));
        return;
      }

      void fetchProgress();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to remove link');
    }
  };

  if (!activeOrgId) {
    return <div className="rounded-xl bg-white p-6">Select an organization to view goals.</div>;
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-100">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-gray-900">Goals / OKRs</div>
            <div className="text-xs text-gray-500">Goals linked to projects/tasks with progress rollups</div>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-white hover:bg-primary/90"
          >
            New
          </button>
        </div>

        <div className="mt-4 space-y-2">
          {loading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : goals.length === 0 ? (
            <div className="text-sm text-gray-500">No goals yet.</div>
          ) : (
            goals.map((g) => (
              <button
                key={g.id}
                onClick={() => setSelectedGoalId(g.id)}
                className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition ${
                  g.id === selectedGoalId ? 'border-primary bg-primary/5' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium text-gray-900">{g.title}</div>
                <div className="text-xs text-gray-500">{g.type} · {g.progress_mode} · {g.status}</div>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="lg:col-span-2 space-y-4">
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          {progressLoading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : !selectedGoal ? (
            <div className="text-sm text-gray-500">Select a goal.</div>
          ) : !progress ? (
            <div className="text-sm text-gray-500">Progress unavailable.</div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-lg font-semibold text-gray-900">{progress.goal.title}</div>
                  <div className="mt-1 text-sm text-gray-600">{progress.goal.type} · {progress.goal.progress_mode}</div>
                </div>
                <button
                  onClick={() => setShowLink(true)}
                  className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Link
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <div className="rounded-xl bg-gray-50 p-3">
                  <div className="text-xs text-gray-500">Progress</div>
                  <div className="text-sm font-semibold text-gray-900">{progress.progress_percent}%</div>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <div className="text-xs text-gray-500">Linked projects</div>
                  <div className="text-sm font-semibold text-gray-900">{progress.linked.projects}</div>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <div className="text-xs text-gray-500">Linked tasks</div>
                  <div className="text-sm font-semibold text-gray-900">{progress.linked.tasks}</div>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <div className="text-xs text-gray-500">Status</div>
                  <div className="text-sm font-semibold text-gray-900">{progress.goal.status}</div>
                </div>
              </div>

              {progress.goal.progress_mode === 'manual' && (
                <div className="rounded-xl border border-gray-200 p-4">
                  <div className="text-sm font-semibold text-gray-900">Manual progress</div>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700">Current</label>
                      <input
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        type="number"
                        min={0}
                        value={Number(selectedGoal.current_value || 0)}
                        onChange={(e) => {
                          const next = goals.map((g) => g.id === selectedGoal.id ? ({ ...g, current_value: Number(e.target.value) } as any) : g);
                          setGoals(next as any);
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700">Target</label>
                      <input
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        type="number"
                        min={0}
                        value={Number(selectedGoal.target_value || 0)}
                        onChange={(e) => {
                          const next = goals.map((g) => g.id === selectedGoal.id ? ({ ...g, target_value: Number(e.target.value) } as any) : g);
                          setGoals(next as any);
                        }}
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <button
                      onClick={saveManualProgress}
                      className="rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-white hover:bg-primary/90"
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}

              <div className="rounded-xl border border-gray-200">
                <div className="border-b border-gray-200 px-4 py-3">
                  <div className="text-sm font-semibold text-gray-900">Links</div>
                </div>
                <div className="divide-y">
                  {progress.links.length === 0 ? (
                    <div className="px-4 py-4 text-sm text-gray-500">No linked items yet.</div>
                  ) : (
                    progress.links.map((l) => (
                      <div key={l.id} className="flex items-center justify-between gap-3 px-4 py-3">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{l.entity_type}</div>
                          <div className="text-xs text-gray-500">{l.entity_id}</div>
                        </div>
                        <button
                          onClick={() => removeLink(l.entity_type, l.entity_id)}
                          className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {(progress.goal.progress_mode === 'linked_projects' || progress.goal.progress_mode === 'linked_tasks') && (
                <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-700">
                  Linking controls determine progress automatically. Use “Link” to attach projects/tasks.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold text-gray-900">New goal / OKR</div>
              <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700">Type</label>
                  <select
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    value={createType}
                    onChange={(e) => setCreateType(e.target.value as any)}
                  >
                    <option value="goal">Goal</option>
                    <option value="okr">OKR</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700">Progress mode</label>
                  <select
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    value={createMode}
                    onChange={(e) => setCreateMode(e.target.value as any)}
                  >
                    <option value="manual">Manual</option>
                    <option value="linked_projects">Linked projects</option>
                    <option value="linked_tasks">Linked tasks</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700">Title</label>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  value={createTitle}
                  onChange={(e) => setCreateTitle(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700">Current</label>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    type="number"
                    min={0}
                    value={createCurrent}
                    onChange={(e) => setCreateCurrent(Number(e.target.value))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700">Target</label>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    type="number"
                    min={0}
                    value={createTarget}
                    onChange={(e) => setCreateTarget(Number(e.target.value))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700">Due date</label>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  type="date"
                  value={createDue}
                  onChange={(e) => setCreateDue(e.target.value)}
                />
              </div>
            </div>

            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setShowCreate(false)}
                className="flex-1 rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createGoal}
                className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {showLink && selectedGoalId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div className="text-lg font-semibold text-gray-900">Link item</div>
              <button onClick={() => setShowLink(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700">Type</label>
                <select
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  value={linkType}
                  onChange={(e) => {
                    setLinkType(e.target.value as any);
                    setLinkEntityId('');
                  }}
                >
                  <option value="project">Project</option>
                  <option value="task">Task</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700">Item</label>
                <select
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  value={linkEntityId}
                  onChange={(e) => setLinkEntityId(e.target.value)}
                >
                  <option value="">Select</option>
                  {linkType === 'project'
                    ? projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)
                    : tasks.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setShowLink(false)}
                className="flex-1 rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={addLink}
                className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
              >
                Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoalsTab;
